import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, EnvelopeSimple, ChatCircle, CheckCircle, XCircle, Warning } from "@phosphor-icons/react";
import DOMPurify from "dompurify";
import { api } from "../../lib/api";

const AUDIENCE_LABEL = {
  all_applied: "All applied",
  all_approved: "All approved",
  guests: "Guests only",
  sponsor_team: "Sponsor team",
  sponsor_request: "Sponsor requests",
  all_subscribers: "Newsletter subscribers",
};

const StatusIcon = ({ status }) => {
  if (status === "sent") return <CheckCircle size={14} weight="bold" className="text-cyber-green" />;
  if (status === "stubbed") return <Warning size={14} weight="bold" className="text-cyber-yellow" />;
  return <XCircle size={14} weight="bold" className="text-cyber-red" />;
};

const AdminBroadcastDetail = () => {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/broadcasts/${id}`);
        if (cancelled) return;
        setB(data);
        // Poll while the background worker is still running
        if (data && (data.status === "queued" || data.status === "running")) {
          timer = setTimeout(load, 2000);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Not found");
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  if (error) return <div className="font-mono-tech text-white/60">{error}</div>;
  if (!b) return <div className="font-mono-tech text-white/40">/ loading...</div>;

  return (
    <div data-testid="broadcast-detail">
      <Link to="/admin/broadcasts" className="font-mono-tech text-xs text-cyber-yellow tracking-[0.2em] uppercase flex items-center gap-2"><ArrowLeft size={12} /> All broadcasts</Link>
      <div className="mt-4">
        <div className="term-label flex items-center gap-2">
          {b.channel === "email" ? <EnvelopeSimple size={12} /> : <ChatCircle size={12} />} / {b.channel} broadcast · {b.status}
        </div>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-2">{b.name}</h1>
        <p className="text-white/60 mt-2 text-sm font-mono-tech">
          {AUDIENCE_LABEL[b.audience] || b.audience}{b.event_title ? ` · ${b.event_title}` : ""} · sent {b.sent_at ? new Date(b.sent_at).toLocaleString() : "—"} by {b.created_by}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        <div className="hard-border bg-cyber-surface p-5">
          <div className="term-label">/ recipients</div>
          <div className="font-display font-black text-4xl mt-2 tracking-tighter text-white">{b.recipient_count}</div>
        </div>
        <div className="hard-border bg-cyber-surface p-5">
          <div className="term-label">/ delivered</div>
          <div className="font-display font-black text-4xl mt-2 tracking-tighter text-cyber-green">{b.sent_count}</div>
        </div>
        <div className="hard-border bg-cyber-surface p-5">
          <div className="term-label">/ stubbed</div>
          <div className="font-display font-black text-4xl mt-2 tracking-tighter text-cyber-yellow">{b.stubbed_count}</div>
          <div className="font-mono-tech text-[10px] text-white/40 mt-1">no provider key set</div>
        </div>
        <div className="hard-border bg-cyber-surface p-5">
          <div className="term-label">/ failed</div>
          <div className="font-display font-black text-4xl mt-2 tracking-tighter text-cyber-red">{b.failed_count}</div>
        </div>
      </div>

      <div className="mt-6 hard-border bg-cyber-surface p-5">
        <div className="term-label">/ message</div>
        {b.channel === "email" && (
          <div className="mt-3">
            <div className="font-mono-tech text-xs text-white/60 mb-3">Subject: <span className="text-white">{b.subject}</span></div>
            <div className="bg-white text-cyber-black p-4 max-h-[500px] overflow-auto">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.body_html || `<pre>${b.body_text || ""}</pre>`) }} />
            </div>
          </div>
        )}
        {b.channel === "sms" && (
          <div className="mt-3 bg-cyber-black p-4">
            <div className="bg-cyber-elevated text-white p-4 max-w-[320px] text-sm leading-relaxed whitespace-pre-line break-words rounded-bl-2xl rounded-tl-2xl rounded-tr-2xl">
              {b.body_text}
            </div>
          </div>
        )}
      </div>

      {b.deliveries && b.deliveries.length > 0 && (
        <div className="mt-6 hard-border bg-cyber-surface p-5">
          <div className="term-label">/ delivery log ({b.deliveries.length})</div>
          <div className="mt-3 max-h-[420px] overflow-auto">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-cyber-line bg-cyber-elevated font-mono-tech text-[10px] tracking-[0.2em] uppercase text-white/60 sticky top-0">
              <div className="col-span-1">Status</div>
              <div className="col-span-3">Name</div>
              <div className="col-span-5">{b.channel === "email" ? "Email" : "Phone"}</div>
              <div className="col-span-3 text-right">When</div>
            </div>
            {b.deliveries.map((d, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-cyber-line/60 items-center text-xs">
                <div className="col-span-1"><StatusIcon status={d.status} /></div>
                <div className="col-span-3 text-white truncate">{d.name || "—"}</div>
                <div className="col-span-5 font-mono-tech text-white/70 truncate">{d.to}</div>
                <div className="col-span-3 text-right font-mono-tech text-white/40">{d.at ? new Date(d.at).toLocaleTimeString() : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBroadcastDetail;
